import { zones } from './json/countries.json';

function getCountryByTimeZone(timeZone: string) {
  const timezone_object = zones[timeZone as keyof typeof zones];

  if (
    !timezone_object ||
    !timezone_object.countries ||
    !timezone_object.countries.length
  ) {
    return 'UNK';
  }

  return timezone_object.countries[0] || 'UNK';
}

export function getBrowserInfo() {
  const language = navigator.language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  let country = '';

  country = getCountryByTimeZone(timezone);

  return { language, timezone, country };
}

declare global {
  interface Window {
    Android?: any;
    webkit?: any;
    isNativeApp?: boolean;
    ReactNativeWebView?: { postMessage: (msg: string) => void };
  }
}

export type WindowWithNativeBridge = Window & {
  ReactNativeWebView: NonNullable<Window['ReactNativeWebView']>;
};

export function isRunningInNativeApp(
  win: Window | undefined = typeof window === 'undefined' ? undefined : window,
): win is WindowWithNativeBridge {
  return (
    win !== undefined &&
    typeof win.ReactNativeWebView?.postMessage === 'function'
  );
}

export function isRunningInIosNativeApp(
  win: Window | undefined = typeof window === 'undefined' ? undefined : window,
): boolean {
  if (!isRunningInNativeApp(win)) {
    return false;
  }

  const nav = win?.navigator;
  const ua = nav?.userAgent || '';

  // iPhone/iPod (e iPad quando a UA ainda expoe "iPad").
  if (/iP(hone|ad|od)/i.test(ua)) {
    return true;
  }

  // iPad: desde o iPadOS 13 o WKWebView manda, por padrao, a UA de Mac
  // (modo desktop) e NAO expoe "iPad" — por isso a deteccao so por UA
  // falhava no iPad. Detecta o iPad como "Macintosh com touch": um Mac
  // real reporta maxTouchPoints 0; o iPad reporta > 0.
  return /Macintosh/i.test(ua) && (nav?.maxTouchPoints ?? 0) > 0;
}

/**
 * Diz se o site NÃO está numa aba comum de navegador: pega bridge do Android,
 * qualquer WKWebView, qualquer WebView do Android e janela em modo standalone.
 *
 * Chamava-se `isRunningInPWA` e o nome mentia (ENG-843): isto acerta o app
 * React Native e também o navegador in-app do Instagram e do Facebook, porque
 * `window.webkit` existe em TODO WKWebView. Não serve para decidir nada que
 * dependa de "é um PWA" — para isso, ver `composables/usePwaShutdown.ts`.
 *
 * Para saber se é o app nativo, use `isRunningInNativeApp()`, que é o único
 * confiável por checar o bridge `ReactNativeWebView`.
 */
export function isRunningInAppLikeContext() {
  if (
    typeof window.Android !== 'undefined' ||
    typeof window.webkit !== 'undefined'
  ) {
    return true;
  }

  const ua = navigator.userAgent || '';

  if (ua.includes('wv') || ua.includes('WebView')) {
    return true;
  }

  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  return (window as any).isNativeApp === true;
}

/**
 * ATENÇÃO: isto NÃO detecta TWA com precisão (ENG-843). O Chrome no Android
 * preenche `document.referrer` com `android-app://<pacote>` em qualquer
 * navegação iniciada por outro app — WhatsApp, Gmail, o app do Google — e não
 * só pelo TWA. Casar o pacote também não separa: o app React Native herdou o
 * pacote do TWA antigo (`br.bet.apostatudo.twa`).
 *
 * Fica como está de propósito. Os dois consumidores toleram o falso-positivo:
 * no `InstallAppPopup` ele só suprime um convite para instalar (a pessoa veio
 * de um app, o convite sobra mesmo), e em `analytics.client.ts` o campo
 * `is_twa` mantém nome e valor para não quebrar a série do BI — mudar o
 * critério aqui mudaria o que a série mede.
 *
 * Por isso a parede do ENG-843 NÃO usa esta função: lá o falso-positivo cobre
 * o site inteiro sem saída. Ver `composables/usePwaShutdown.ts`.
 */
export function isRunningInTWA() {
  return (
    document.referrer.includes('app_source=app&standalone=true') ||
    document.referrer.startsWith('android-app://')
  );
}
